/**
 * Public consultation domain helpers — shared by /consultations, the detail
 * page, the submission API route and the admin surface.
 *
 * MCC-100 cl. 0054: every project design document goes to public consultation
 * for a minimum of 30 calendar days before the programme decides on it. The
 * database enforces the minimum period (trg_consultation_minimum_period); this
 * module carries the public-facing rules that sit on top of it:
 *
 *   - a consultation ACCEPTS submissions only while its status is OPEN and
 *     today is inside [openDate, closeDate];
 *   - a STAYED consultation is neither open nor closed: it stopped taking
 *     comment because the instrument it amends is being replaced, it carries
 *     no closing protocol and no decision, and it says so on its own page
 *     (migration 20260903170000_consultation_stay);
 *   - a WITHDRAWN consultation is a stay disposed of: the proposal is gone,
 *     not decided on its merits, and the page owes the reader the reason and
 *     what now carries whatever the proposal would have delivered
 *     (migration 20260907120000_consultation_withdrawal, MCC-WDR-2026-001);
 *   - a consultation on a demonstration project is displayed (badged) but
 *     never accepts submissions — a public record of comments on a project
 *     that does not exist is worse than none;
 *   - the closing protocol (summary) is public only once published.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export type ConsultationStatus = 'OPEN' | 'CLOSED' | 'STAYED' | 'WITHDRAWN' | string;

export type ConsultationKind = 'PROJECT' | 'RULE_CHANGE';

export interface ConsultationRow {
  id: string;
  /** Null for RULE_CHANGE consultations. */
  projectId: string | null;
  status: ConsultationStatus;
  openDate: string | null;
  closeDate: string | null;
  // No comment count here, deliberately: the row's stored counter is not read
  // for display. Counts come from countConsultationComments() below.
  summary?: string | null;
  summaryPublishedAt?: string | null;
  /** Rule Change and Public Consultation Procedure v1.0 §4.5. Four elements,
   *  four fields: a single string could hold all four and did, which is how the
   *  public page came to render a placeholder written for project consultations
   *  under the heading "Closing protocol". */
  protocolCommentCounts?: Record<string, number> | null;
  protocolResponses?: string | null;
  protocolChanges?: string | null;
  protocolRecommendation?: string | null;
  protocolPublishedAt?: string | null;
  /**
   * PROJECT: a project design document (MCC-100 cl. 0054).
   * RULE_CHANGE: an instrument under MCC-001 cl. 0079–0083 — the proposal text
   * is served from lib/consultation-proposals.ts by proposalSlug and its
   * SHA-256 is fixed on the row at opening.
   */
  kind?: ConsultationKind | null;
  title?: string | null;
  instrument?: string | null;
  tier?: number | null;
  noticeDays?: number | null;
  proposalSlug?: string | null;
  proposalSha256?: string | null;
  decisionBody?: string | null;
  description?: string | null;
  /** Set together, and only when status is STAYED — enforced by CHECK constraint. */
  stayedAt?: string | null;
  stayReason?: string | null;
  stayDecisionRef?: string | null;
  /**
   * Set together, and only when status is WITHDRAWN — enforced by CHECK
   * constraint on the first three. A withdrawn row keeps its stay columns:
   * the 3 September record is not overwritten by the disposal.
   */
  withdrawnAt?: string | null;
  withdrawalReason?: string | null;
  withdrawalDecisionRef?: string | null;
  /**
   * What the proposal would have delivered and which instrument must now
   * carry it. Null only where nothing is lost — which is rare, and is a
   * claim the withdrawing instrument has to stand behind.
   */
  withdrawalCarriedBy?: string | null;
}

export function isRuleChange(c: Pick<ConsultationRow, 'kind'>): boolean {
  return (c.kind ?? 'PROJECT') === 'RULE_CHANGE';
}

/**
 * Stayed: stopped, but not decided. The distinction matters on the public
 * page — a closed consultation owes the reader a closing protocol and a
 * decision, and a stayed one owes them a reason and a route back.
 */
export function isStayed(c: Pick<ConsultationRow, 'status'>): boolean {
  return (c.status || '').toUpperCase() === 'STAYED';
}

/**
 * Withdrawn: the proposal is gone, and not because it lost on the merits.
 * Distinct from CLOSED for the same reason STAYED is — a closed consultation
 * owes a closing protocol and a decision; a withdrawn one owes a reason and
 * the name of whatever now carries what the proposal would have delivered.
 */
export function isWithdrawn(c: Pick<ConsultationRow, 'status'>): boolean {
  return (c.status || '').toUpperCase() === 'WITHDRAWN';
}

/** Stopped, either way — stayed or withdrawn. Neither accepts submissions. */
export function isHalted(c: Pick<ConsultationRow, 'status'>): boolean {
  return isStayed(c) || isWithdrawn(c);
}

/**
 * The label the status pill shows.
 *
 * The last line used to be `return (c.status || 'CLOSED').toUpperCase()`, and
 * for a row whose close date has passed while nobody pressed the close button
 * that returned 'OPEN' — inside the page's "Closed consultations" section, on a
 * closed-grey pill, above a line reading "This consultation closed on <date>".
 * Three surfaces, two answers. `isAcceptingSubmissions` already refuses the
 * comment, so the defect was never that someone could comment; it was that the
 * page said OPEN about something it had itself closed.
 *
 * Nothing in this Programme flips that column on a schedule — there is no cron,
 * no trigger and no job (MCC-CLS-2026-001 Part C). So a row sitting at OPEN with
 * a past close date is the normal state, not an anomaly, and the label has to
 * tell the truth about it without waiting for a human.
 */
export function statusLabel(c: Pick<ConsultationRow, 'status' | 'openDate' | 'closeDate'>, now = new Date()): string {
  if (isAcceptingSubmissions(c, now)) return 'OPEN';
  if (isWithdrawn(c)) return 'WITHDRAWN';
  if (isStayed(c)) return 'STAYED';
  // Past its close date but never formally closed: say so, rather than 'OPEN'.
  if ((c.status || '').toUpperCase() === 'OPEN') return 'CLOSED — PROTOCOL OWED';
  return (c.status || 'CLOSED').toUpperCase();
}

export interface ConsultationProject {
  id: string;
  name: string;
  projectCode?: string | null;
  country?: string | null;
  description?: string | null;
  methodologyCode?: string | null;
  is_demonstration?: boolean | null;
}

/**
 * Read from consultation_comments_public, not the base table: the base
 * table's public policy was dropped (20260914130000) because it exposed
 * authorName, which Transparency Policy §2.10 requires published
 * "anonymized" and the comment form captures no consent to publish. The view
 * carries authorOrg but never a commenter's name or email.
 */
export interface PublicComment {
  id: string;
  consultationId: string;
  projectId: string | null;
  authorOrg: string | null;
  category: string | null;
  content: string;
  createdAt: string;
  staffResponse: string | null;
  respondedAt: string | null;
}

/**
 * The number of comments on each consultation, counted by the database when
 * the page is read. Never the counter stored on the consultation row.
 *
 * WHY NOT THE STORED COUNTER
 *
 * stakeholder_consultations carries a comment counter kept by the trigger
 * trg_consultation_comment_count, which fires AFTER INSERT only: nothing
 * subtracts a comment that is deleted or taken out of publication. On 10
 * September 2026 the self-test of migration 20260914130000 inserted a probe
 * comment on the stayed MCC-800 Amendment 1 consultation and deleted it in the
 * same transaction. The counter stayed at 1, and from then until this count
 * replaced it /consultations printed "1 public comment" for a consultation that
 * had none, with the comments table empty (found by the audit of 29 September
 * 2026, D10; served_defect SD-181). A count is a measurement; the counter is an
 * assertion nobody re-checks.
 *
 * WHAT IS COUNTED
 *
 *   'public'  rows of consultation_comments_public: the comments the public
 *             record carries. The view is the base table filtered on
 *             isPublic = true and nothing else (20260914130000), so this is
 *             exactly what a "public comments" label claims, and it is the only
 *             count an anonymous reader's client can make: the base table is
 *             readable by register staff alone.
 *   'all'     rows of consultation_comments, public or not, which is what a
 *             staff "comments" label claims. Only meaningful with a client that
 *             can read every row, i.e. the service-role client after a staff
 *             role check. Under RLS it would count what the caller may see
 *             (three staff roles can read the table, not every role the staff
 *             list admits), and print that as the total.
 *
 * Returns null when the counts could not be read, and leaves out any
 * consultation whose count did not come back as a number. A caller says the
 * count is unavailable in either case. It never falls back to the counter.
 */
export async function countConsultationComments(
  supabase: SupabaseClient,
  source: 'public' | 'all',
  scope: { projectId?: string } = {},
): Promise<Map<string, number> | null> {
  const relation = source === 'public' ? 'consultation_comments_public' : 'consultation_comments';
  try {
    let query = supabase.from('stakeholder_consultations').select(`id, ${relation}(count)`);
    if (scope.projectId) query = query.eq('projectId', scope.projectId);
    const { data, error } = await query;
    if (error || !data) {
      // eslint-disable-next-line no-console
      console.error(`[consultations] ${source} comment count could not be read`, error);
      return null;
    }
    const counts = new Map<string, number>();
    for (const row of data as unknown as Array<Record<string, unknown>>) {
      const embedded = row[relation];
      const n = Array.isArray(embedded) ? (embedded[0] as { count?: unknown } | undefined)?.count : undefined;
      if (typeof row.id === 'string' && typeof n === 'number') counts.set(row.id, n);
    }
    return counts;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[consultations] ${source} comment count could not be read`, err);
    return null;
  }
}

export const COMMENT_CATEGORIES = [
  'GENERAL',
  'ENVIRONMENTAL',
  'SOCIAL',
  'METHODOLOGY',
  'SAFEGUARDS',
  'OTHER',
] as const;
export type CommentCategory = (typeof COMMENT_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<CommentCategory, string> = {
  GENERAL: 'General',
  ENVIRONMENTAL: 'Environmental impact',
  SOCIAL: 'Social impact and rights',
  METHODOLOGY: 'Methodology and quantification',
  SAFEGUARDS: 'Safeguards',
  OTHER: 'Other',
};

export const COMMENT_LIMITS = {
  authorName: 200,
  authorEmail: 320,
  authorOrg: 200,
  content: 5000,
} as const;

/** Minimum consultation period in calendar days (MCC-100 cl. 0054). */
export const MINIMUM_CONSULTATION_DAYS = 30;

export function daysBetween(from: Date, to: Date): number {
  return Math.ceil((to.getTime() - from.getTime()) / 86_400_000);
}

/** Days until close, negative once past. Null when no closeDate. */
export function daysRemaining(c: Pick<ConsultationRow, 'closeDate'>, now = new Date()): number | null {
  if (!c.closeDate) return null;
  return daysBetween(now, new Date(c.closeDate));
}

/**
 * Whether the consultation accepts submissions right now. Status alone is not
 * enough: the seeded data holds rows whose status is OPEN with closeDate in the
 * past, and a status column that nobody flips is not a consent window.
 */
export function isAcceptingSubmissions(
  c: Pick<ConsultationRow, 'status' | 'openDate' | 'closeDate'>,
  now = new Date(),
): boolean {
  if ((c.status || '').toUpperCase() !== 'OPEN') return false;
  if (c.openDate && new Date(c.openDate) > now) return false;
  if (c.closeDate && new Date(c.closeDate) < now) return false;
  return true;
}

export function isCategory(value: unknown): value is CommentCategory {
  return typeof value === 'string' && (COMMENT_CATEGORIES as readonly string[]).includes(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
